/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { find, isEmpty } from 'lodash';
import { Loader, Message } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';

import { Modal } from '../../../../../components';
import EditInvoice from '../../../containers/details/invoice/EditInvoice';
import { MASTER_APPOINTMENT_INVOICE_FRAGMENT } from '../../../containers/masterAccessQueries';

import type { Appointment } from '../../../../../type';

// Query to get invoice of appointment
export const GET_INVOICE_QUERY = gql`
    query getInvoice(
        $appointment: ID!
        $salon: ID!
        $lang: String!
        $client: ID
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    appointment(id: $appointment) {
                        id
                        invoice {
                            id
                            items {
                                id
                                type
                                entity {
                                    ... on Service {
                                        id
                                        name(lang: $lang)
                                    }
                                    ... on Product {
                                        id
                                        name
                                    }
                                }
                                seller
                                sellerType
                                quantity
                                price
                                paymentType
                                appointment {
                                    id
                                }
                            }
                            totalPrice
                            paid
                        }
                    }
                    memberships(client: $client) {
                        id
                        client {
                            id
                            firstName
                            lastName
                        }
                        author {
                            id
                            firstName
                            lastName
                        }
                        createdAt
                        serialNumber
                        service {
                            id
                            name(lang: $lang)
                            duration
                            price
                        }
                        quantity
                        price
                        pricePerUse
                        totalUses
                    }
                }
            }
            ...onMasterAppointmentInvoice
        }
    }
    ${MASTER_APPOINTMENT_INVOICE_FRAGMENT}
`;

// Fix styling of spinner inside modal window
const Spinner = styled(Loader)`
    &::before {
        border: 0.2em solid rgba(0, 0, 0, 0.1) !important;
    }
    &::after {
        border-color: #767676 transparent transparent !important;
    }
`;

type AppointmentInvoiceProps = {
    appointment: Appointment,
};

const AppointmentInvoice = ({
    appointment,
    ...props
}: AppointmentInvoiceProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    const { loading, data } = useQuery(GET_INVOICE_QUERY, {
        variables: {
            appointment: appointment.id,
            salon,
            lang: currentLanguage,
            client: appointment.client.id,
        },
        fetchPolicy: 'cache-and-network',
    });

    const viewer = data && data.viewer;
    const memberships = !loading && viewer ? viewer.salon.memberships : [];

    let membership = null;
    if (appointment.membership) {
        if (!isEmpty(memberships)) {
            membership = find(memberships, {
                id: appointment.membership.id,
            });
        }
    } else if (
        !appointment.membership &&
        !isEmpty(memberships) &&
        appointment.invoice &&
        !appointment.invoice.paid
    ) {
        membership =
            find(
                memberships,
                i =>
                    i.client.id === appointment.client.id &&
                    i.service.id === appointment.service.id &&
                    i.totalUses < i.quantity,
            ) || null;
    }

    if (loading) {
        return (
            <Modal.Context>
                <Modal.Content>
                    <Spinner inline="centered" size="big" active />
                </Modal.Content>
            </Modal.Context>
        );
    }

    const invoice = viewer ? viewer.salon.appointment.invoice : null;

    if (invoice) {
        return (
            <EditInvoice
                invoice={invoice}
                membership={membership}
                appointment={appointment}
                {...props}
            />
        );
    }

    return (
        <Modal.Context>
            <Modal.Content>
                <Message>
                    <FormattedMessage
                        id="pages.appointments.details.invoice.AppointmentInvoice.no_invoice"
                        defaultMessage="This appointment has no invoice yet."
                    />
                </Message>
            </Modal.Content>
        </Modal.Context>
    );
};

export default AppointmentInvoice;
